// sentry.config.ts
import type { Event, EventHint } from '@sentry/types';

export const isProd = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === 'PRD';

// --- Feature flags (toggles) ---
export const SENTRY_ENABLED =
  (process.env.NEXT_PUBLIC_SENTRY_ENABLED ?? 'true') === 'true';

// SDK-side error sampling (0..1). E.g.: 0.25 = 25% of errors in prod
export const ERROR_SAMPLE_RATE = Number(
  process.env.NEXT_PUBLIC_SENTRY_ERROR_SAMPLE_RATE ?? (isProd ? '0.5' : '1'),
);

// Per-session/minute guard rails (simple token bucket)
export const MAX_EVENTS_PER_MINUTE = Number(
  process.env.NEXT_PUBLIC_SENTRY_MAX_EVENTS_PER_MINUTE ?? '30',
);
export const MAX_EVENTS_PER_SESSION = Number(
  process.env.NEXT_PUBLIC_SENTRY_MAX_EVENTS_PER_SESSION ?? '100',
);

// Toggle to capture console.error as events
export const CAPTURE_CONSOLE =
  (process.env.NEXT_PUBLIC_SENTRY_CAPTURE_CONSOLE ?? 'false') === 'true';

// Non-payment errors are opt-in so they cannot exhaust the quota reserved for
// payment monitoring. Sampling and rate limits still apply when re-enabled.
export const CAPTURE_NON_PAYMENT_ERRORS =
  (process.env.NEXT_PUBLIC_SENTRY_CAPTURE_NON_PAYMENT_ERRORS ?? 'false') ===
  'true';

export const isMessageOnlyEvent = (
  event: Pick<Event, 'exception' | 'message'>,
) => !event.exception && !!event.message;

export const isPaymentMonitoringEvent = (event: Event): boolean => {
  if (event.tags?.['payment.workflow'] === 'checkout') {
    return true;
  }

  if (event.fingerprint?.[0] === 'payment-workflow-anomaly') {
    return true;
  }

  return (
    event.exception?.values?.some(
      (value) => value.type === 'PaymentWorkflowAnomaly',
    ) ?? false
  );
};

type ErrorSamplingOptions = {
  captureNonPaymentErrors?: boolean;
  random?: () => number;
  sampleRate?: number;
};

export const shouldSampleEvent = (
  event: Event,
  {
    captureNonPaymentErrors = CAPTURE_NON_PAYMENT_ERRORS,
    random = Math.random,
    sampleRate = ERROR_SAMPLE_RATE,
  }: ErrorSamplingOptions = {},
): boolean => {
  if (isPaymentMonitoringEvent(event)) {
    return true;
  }

  if (!captureNonPaymentErrors || !Number.isFinite(sampleRate)) {
    return false;
  }

  if (sampleRate >= 1) {
    return true;
  }

  if (sampleRate <= 0) {
    return false;
  }

  return random() < sampleRate;
};

// Allowlist of own origins (reduces noise from third parties/extensions)
export const ALLOW_URLS = [
  /https?:\/\/([a-z0-9-]+\.)?breitling\.com/i,
  /https?:\/\/([a-z0-9-]+\.)?gallet\.com/i,
  /https?:\/\/.*\.vercel\.app/i,
];

// --- Local limiter state (per tab session) ---

/**
 * List of noisy/unactionable error patterns to ignore.
 * Includes network interruptions, script load failures, and browser-specific noise.
 */
export const COMMON_IGNORE_ERRORS = [
  'Sentry error ID',
  /AbortError/i,
  /Non-Error promise rejection captured/i,
];

/**
 * Client-side specific error patterns to ignore
 */
export const CLIENT_IGNORE_ERRORS = [
  ...COMMON_IGNORE_ERRORS,
  /NetworkError when attempting to fetch resource/i,
  /Loading chunk \d+ failed/i,
  /chunk load error/i,
  /Load failed/i,
  /Failed to fetch/i,
  /Failed to load image/i,
  /ResizeObserver loop limit exceeded/i,
  /Could not load parcelLab script dynamically/i,
  /Loading script error - .*parcellab\.com\/js\/v5\/main\.min\.js/i,
  /Cannot assign to read only property '(message|debug)'/i,
  /before send for type `error` returned `null`/i,
  /An event processor returned `null`/i,
  /google_tag_manager\[/i,
  /Can't find variable: ttq/i,
  /Invalid call to runtime\.sendMessage\(\)\. Tab not found/i,
  /Attempt to (get a record from|delete range from) database without an in-progress transaction/i,
  /Database deleted by request of the user/i,
];

/**
 * Server/Edge specific error patterns to ignore
 * These are safe to ignore as they represent normal client disconnection behavior
 * and don't indicate problems with the backend
 */
export const SERVER_IGNORE_ERRORS = [
  ...COMMON_IGNORE_ERRORS,
  // Client disconnection errors (normal behavior when client closes connection)
  /ECONNRESET/i,
  /socket hang up/i,
];

/**
 * Checks if a message or error string matches any noisy error patterns.
 */
const isNoisyMessage = (
  msg: string | undefined,
  patterns: (string | RegExp)[],
): boolean =>
  !!msg &&
  patterns.some((pattern) =>
    typeof pattern === 'string' ? msg.includes(pattern) : pattern.test(msg),
  );

// Common deny URLs patterns
export const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /chrome-extension:\/\//i,
  /safari-web-extension:/i,
];

// Common base configuration values
export const COMMON_CONFIG = {
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT_NAME,
  // Same value as package.json.version - will be replaced by pipeline with APP_VERSION
  release: '25.0.38',
  debug: JSON.parse(process.env.NEXT_PUBLIC_SENTRY_DEBUG || 'false'),
  normalizeDepth: 10,
  attachStacktrace: true,
  maxBreadcrumbs: 50,
} as const;

// Client-side rate limiting state (session-based)
// Note: This is client-side only. Server/Edge use stateless rate limiting.

// --- Local limiter state (per tab session) ---
let minuteCount = 0;
let sessionCount = 0;
let minuteWindow = Date.now();
export function underBudget() {
  const now = Date.now();
  if (now - minuteWindow > 60_000) {
    minuteWindow = now;
    minuteCount = 0;
  }
  return (
    minuteCount < MAX_EVENTS_PER_MINUTE && sessionCount < MAX_EVENTS_PER_SESSION
  );
}
export function bumpBudget() {
  minuteCount++;
  sessionCount++;
}

// Server/Edge rate limiting configuration

// These limits are per-instance and work with stateless environments

export const MAX_EVENTS_PER_MINUTE_SERVER = Number(
  process.env.NEXT_PUBLIC_SENTRY_MAX_EVENTS_PER_MINUTE_SERVER ?? '50',
);

export const MAX_EVENTS_PER_HOUR_SERVER = Number(
  process.env.NEXT_PUBLIC_SENTRY_MAX_EVENTS_PER_HOUR_SERVER ?? '500',
);

// Maximum cache size to prevent memory leaks in Edge Runtime
// Edge Runtime has limited memory (~128MB), so we limit cache entries

export const MAX_CACHE_ENTRIES = Number(
  process.env.NEXT_PUBLIC_SENTRY_MAX_CACHE_ENTRIES ?? '1000',
);

// Detect if we're running in Edge Runtime
// Edge Runtime doesn't support setInterval, process.on, or long-lived timers
function isEdgeRuntime(): boolean {
  // Check for Next.js Edge Runtime environment variable
  if (typeof process !== 'undefined' && process.env.NEXT_RUNTIME === 'edge') {
    return true;
  }

  // Check for Vercel Edge Runtime (uses Deno)
  if (typeof globalThis !== 'undefined') {
    try {
      // Deno is available in Edge Runtime
      if ('Deno' in globalThis) {
        return true;
      }
    } catch {
      // Ignore errors in detection
    }
  }

  // Check for EdgeRuntime global (if available)
  try {
    if (typeof (globalThis as any).EdgeRuntime !== 'undefined') {
      return true;
    }
  } catch {
    // Ignore errors in detection
  }

  return false;
}

// Stateless rate limiting for server/edge using deterministic sampling
// This uses a hash-based approach that works across stateless instances
// Edge Runtime compatible: no setInterval, no process.on, limited memory
export function createStatelessRateLimiter(): {
  shouldSend: (event: Event, hint: EventHint) => boolean;
} {
  const isEdge = isEdgeRuntime();
  // Use a simple in-memory cache with TTL for rate limiting
  // This is per-instance, so it works well for serverless/edge functions
  const eventCache = new Map<string, number[]>();
  let lastCleanup = Date.now();
  const CLEANUP_INTERVAL = 300000; // 5 minutes

  // Cleanup function (called on-demand to avoid setInterval in Edge Runtime)
  function cleanupOldEntries(): void {
    const now = Date.now();
    const entriesToDelete: string[] = [];

    eventCache.forEach((timestamps: number[], key: string) => {
      const filtered = timestamps.filter((ts: number) => now - ts < 3600000); // Keep last hour
      if (filtered.length === 0) {
        entriesToDelete.push(key);
      } else {
        eventCache.set(key, filtered);
      }
    });

    entriesToDelete.forEach((key) => eventCache.delete(key));

    // Enforce maximum cache size to prevent memory leaks
    if (eventCache.size > MAX_CACHE_ENTRIES) {
      const entriesArray = Array.from(eventCache.entries());
      // Sort by most recent timestamp (keep newest entries)
      entriesArray.sort((a, b) => {
        const aMax = Math.max(...a[1]);
        const bMax = Math.max(...b[1]);
        return bMax - aMax;
      });
      // Delete oldest entries
      const toDelete = entriesArray.slice(MAX_CACHE_ENTRIES);
      toDelete.forEach(([key]) => eventCache.delete(key));
    }
  }

  // Periodic cleanup for Server Runtime only (Edge uses on-demand cleanup)
  let cleanupInterval: ReturnType<typeof setInterval> | null = null;
  if (!isEdge && typeof setInterval !== 'undefined') {
    cleanupInterval = setInterval(() => {
      cleanupOldEntries();
    }, CLEANUP_INTERVAL);
  }

  // Cleanup on process exit (Server Runtime only)
  if (
    !isEdge &&
    typeof process !== 'undefined' &&
    typeof process.on === 'function' &&
    cleanupInterval
  ) {
    const cleanup = () => {
      if (cleanupInterval) {
        clearInterval(cleanupInterval);
      }
    };
    try {
      process.on('SIGTERM', cleanup);
      process.on('SIGINT', cleanup);
    } catch (error) {
      // Ignore errors in Edge Runtime or environments without process.on
    }
  }

  return {
    shouldSend(event: Event): boolean {
      const now = Date.now();

      // On-demand cleanup for Edge Runtime (every 5 minutes or when cache is too large)
      if (isEdge) {
        if (
          now - lastCleanup > CLEANUP_INTERVAL ||
          eventCache.size > MAX_CACHE_ENTRIES
        ) {
          cleanupOldEntries();
          lastCleanup = now;
        }
      }

      // Create a fingerprint for the error to group similar errors
      const fingerprint = event.fingerprint
        ? event.fingerprint.join('|')
        : event.exception && event.exception.values && event.exception.values[0]
          ? `${event.exception.values[0].type || 'unknown'}:${
              event.exception.values[0].value || 'unknown'
            }`
          : event.message || 'unknown';

      // Get or create timestamp array for this fingerprint
      let timestamps = eventCache.get(fingerprint) || [];

      // Filter out timestamps older than 1 hour (single pass optimization)
      const oneHourAgo = now - 3600000;
      const oneMinuteAgo = now - 60000;
      timestamps = timestamps.filter((ts) => ts > oneHourAgo);

      // Count events in the last minute (using already filtered array)
      const recentMinute = timestamps.filter((ts) => ts > oneMinuteAgo).length;
      // Count events in the last hour (already filtered)
      const recentHour = timestamps.length;

      // Check rate limits
      if (recentMinute >= MAX_EVENTS_PER_MINUTE_SERVER) {
        return false; // Exceeded per-minute limit
      }
      if (recentHour >= MAX_EVENTS_PER_HOUR_SERVER) {
        return false; // Exceeded per-hour limit
      }

      // Add current timestamp
      timestamps.push(now);
      eventCache.set(fingerprint, timestamps);

      return true;
    },
  };
}

// Helper function for dropping noisy load/console events
export function shouldDropNoisyLoadOrConsoleEvents(
  event: Event,
  hint: EventHint,
  patterns: (string | RegExp)[],
): boolean {
  // 1. Check if the main message is noise
  if (isNoisyMessage(event.message, patterns)) {
    return true;
  }

  // 2. Check if any exception values are noise
  const exceptionValues = event.exception?.values;
  if (Array.isArray(exceptionValues)) {
    const hasNoisyException = exceptionValues.some((value) => {
      const type = value.type || '';
      const message = value.value || '';
      const combined = `${type}: ${message}`;
      return (
        isNoisyMessage(combined, patterns) || isNoisyMessage(message, patterns)
      );
    });

    if (hasNoisyException) {
      return true;
    }
  }

  // 3. Noisy chunk/network errors (double firewall)
  const originalMsg =
    typeof hint?.originalException === 'string'
      ? hint?.originalException
      : (hint?.originalException as Error | undefined)?.message;

  if (isNoisyMessage(originalMsg, patterns)) {
    return true;
  }

  return false;
}
